import { useCallback, useEffect, useRef, useState } from 'react';

export interface MarqueeRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

type Mode = 'replace' | 'add' | 'toggle';

interface DragState {
  startX: number;
  startY: number;
  baseSet: Set<string>;
  mode: Mode;
  startedOnRow: boolean;
  started: boolean;
}

const DRAG_THRESHOLD = 4;

const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="toolbar"], [data-no-marquee]';

function modeFor(e: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }): Mode {
  if (e.shiftKey) return 'add';
  if (e.metaKey || e.ctrlKey) return 'toggle';
  return 'replace';
}

export interface UseMarqueeSelectionResult {
  selectedIds: Set<string>;
  marqueeRect: MarqueeRect | null;
  isDragging: boolean;
  /**
   * True only briefly between a drag's mouseup and the synthetic click that follows,
   * so a row's onClick can skip its default expand/collapse on drag-end.
   */
  wasDragRef: React.MutableRefObject<boolean>;
  onMouseDown: (e: React.MouseEvent) => void;
  clear: () => void;
  toggle: (id: string) => void;
}

export function useMarqueeSelection(
  containerRef: React.RefObject<HTMLElement | null>,
  events: { id: string }[],
): UseMarqueeSelectionResult {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [marqueeRect, setMarqueeRect] = useState<MarqueeRect | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const wasDragRef = useRef(false);
  const dragStateRef = useRef<DragState | null>(null);

  // Prune ids that are no longer present (e.g. events array shrinks).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(events.map((e) => e.id));
      const next = new Set<string>();
      let changed = false;
      for (const id of prev) {
        if (valid.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [events]);

  const clear = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const container = containerRef.current;
      if (!container) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(INTERACTIVE_SELECTOR)) return;

      const startedOnRow = !!target.closest('[data-event-id]');

      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        baseSet: new Set(selectedIds),
        mode: modeFor(e),
        startedOnRow,
        started: false,
      };
      wasDragRef.current = false;
    },
    [containerRef, selectedIds],
  );

  useEffect(() => {
    function handleMove(e: MouseEvent) {
      const drag = dragStateRef.current;
      if (!drag) return;
      const container = containerRef.current;
      if (!container) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      if (!drag.started) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        drag.started = true;
        setIsDragging(true);
        // Suppress text selection that may have started before the threshold.
        const sel = window.getSelection();
        if (sel) sel.removeAllRanges();
      }

      e.preventDefault();

      const left = Math.min(drag.startX, e.clientX);
      const top = Math.min(drag.startY, e.clientY);
      const width = Math.abs(dx);
      const height = Math.abs(dy);

      const cRect = container.getBoundingClientRect();
      setMarqueeRect({
        left: left - cRect.left,
        top: top - cRect.top,
        width,
        height,
      });

      const rectAbs = { left, top, right: left + width, bottom: top + height };
      const intersected = new Set<string>();
      const rows = container.querySelectorAll<HTMLElement>('[data-event-id]');
      rows.forEach((node) => {
        const r = node.getBoundingClientRect();
        if (
          r.left < rectAbs.right &&
          r.right > rectAbs.left &&
          r.top < rectAbs.bottom &&
          r.bottom > rectAbs.top
        ) {
          const id = node.getAttribute('data-event-id');
          if (id) intersected.add(id);
        }
      });

      let next: Set<string>;
      if (drag.mode === 'add') {
        next = new Set(drag.baseSet);
        intersected.forEach((id) => next.add(id));
      } else if (drag.mode === 'toggle') {
        next = new Set(drag.baseSet);
        intersected.forEach((id) => {
          if (next.has(id)) next.delete(id);
          else next.add(id);
        });
      } else {
        next = intersected;
      }
      setSelectedIds(next);
    }

    function handleUp() {
      const drag = dragStateRef.current;
      if (!drag) return;
      dragStateRef.current = null;
      setMarqueeRect(null);

      if (drag.started) {
        setIsDragging(false);
        wasDragRef.current = true;
        // Reset after the synthetic click that follows mouseup has had a chance to read it.
        setTimeout(() => {
          wasDragRef.current = false;
        }, 0);
      } else if (!drag.startedOnRow) {
        // Bare click on empty space clears selection.
        setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
      }
    }

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [containerRef]);

  return { selectedIds, marqueeRect, isDragging, wasDragRef, onMouseDown, clear, toggle };
}
